import { useEffect } from "react";
import { useStyles } from "../../../Styles/styles";
import styles from './SizeAndWeightStyles.module.css';
import { TextField } from "@material-ui/core";
import { formatOfficerName, isTicketWarning } from "../../../utils/utils";
import CheckboxWithLabel from "../../CheckboxWithLabel";
import WrappedKeyboardDateTimePicker from "../../WrappedKeyboardDateTimePicker";
import { ADD_OR_UPDATE_SCALE_IN_SIZE_AND_WEIGHT } from "../Mutations/AddOrUpdateScaleInSizeAndWeight";
import { useMutation } from "@apollo/client";
import CollapsingHeader from "./CollapsingHeader/CollapsingHeader";
import { REMOVE_SCALES_FROM_SIZE_AND_WEIGHT } from "../Mutations/RemoveScaleFromSizeAndWeight";
import Required from "./Required/Required";
import CustomAutoComplete from "../../CustomAutoComplete";
import _ from 'lodash';

const OutcomeSection = ({
  outcome,
  handleChange,
  handleDateChange,
  userRoles,
  weightViolation,
  disabled,
  incidentPeople,
  incidentOfficers = [],
  primaryOfficer,
  sizeAndWeightId,
  dispatch,
  userId,
  setPausePolling,
  missingFields,
  nightMode,
  ...props }) => {
  const { violationIdentified, courtAction, offloadRedistribution, scales, endedAt } = outcome || {};
  const classes = useStyles();

  const officerScales = incidentOfficers?.flatMap(officer => {
    const scales = officer?.deptAttribute?.scales || [];
    return scales.map(scale => ({
      ...scale,
      user: formatOfficerName(officer),
    }));
  }) || [];

  const [addScale] = useMutation(ADD_OR_UPDATE_SCALE_IN_SIZE_AND_WEIGHT, {
    context: { serializationKey: "incidentQueue" },
  })
  const [removeScale] = useMutation(REMOVE_SCALES_FROM_SIZE_AND_WEIGHT, {
    context: { serializationKey: "incidentQueue" },
  })

  const updateScales = (arr) => {
    if (!sizeAndWeightId) return null;
    dispatch({ type: "UPDATE_PROPERTY", payload: { property: 'scales', value: arr } })

    if (arr.length > scales?.length) {
      // A scale was added
      const addedScale = arr.find(scale => !scales?.some(s => s.id === scale.id));
      if (addedScale) {
        const scaleId = addedScale.id;
        try {
          addScale({
            variables: {
              input: {
                sizeAndWeightId,
                scaleId,
                originatorId: userId,
              }
            }
          });
        } catch (e) {
          console.error('Error adding axle to axle group', e);
        }
      }
    } else if (arr.length < scales?.length) {
      // A scale was removed
      const removedScale = scales?.find(scale => !arr.some(s => s.id === scale.id));
      if (removedScale) {
        const scaleId = removedScale.id;
        try {
          removeScale({
            variables: {
              input: {
                sizeAndWeightId,
                scaleId,
                originatorId: userId,
              }
            }
          });
        } catch (e) {
          console.error('Error removing axle from axle group', e);
        }
      }
    }
  };

  useEffect(() => {
    if (weightViolation?.ticketViolationPrototype) {
      const isTicket = !isTicketWarning(weightViolation?.ticketViolationPrototype)
      if (!violationIdentified) {
        handleChange(true, 'UPDATE_PROPERTY', { property: 'violationIdentified' })
      }
      if (isTicket !== courtAction) {
        handleChange(isTicket, 'UPDATE_PROPERTY', { property: 'courtAction' })
      }
    }
  }, [handleChange, weightViolation?.ticketViolationPrototype, violationIdentified, courtAction])

  return (
    <CollapsingHeader nightMode={nightMode} title={"Outcome"} defaultOpen={true}>
      <div className={`${styles['flexRow']}`}>
        <Required
          required={missingFields.some(field => field?.name === "violationsIdentified")}
          message={missingFields.find(field => field?.name === "violationsIdentified")?.error}
        >
          <CheckboxWithLabel
            formStyle={classes.formControl}
            checked={violationIdentified}
            onChange={(e) => handleChange(e.target.checked, 'UPDATE_PROPERTY', { property: 'violationIdentified' })}
            stateName={"violationIdentified"}
            title={'Violation\xa0Identified'}
            disabled={!!weightViolation?.ticketViolationPrototype || disabled}
            userRoles={userRoles}
            {...props}
          />
        </Required>
        <Required
          required={missingFields.some(field => field?.name === "courtAction")}
          message={missingFields.find(field => field?.name === "courtAction")?.error}
        >
          <CheckboxWithLabel
            formStyle={classes.formControl}
            checked={courtAction}
            onChange={(e) => handleChange(e.target.checked, 'UPDATE_PROPERTY', { property: 'courtAction' })}
            stateName={"courtAction"}
            title={'Court\xa0Action'}
            disabled={!!weightViolation?.ticketViolationPrototype || disabled}
            userRoles={userRoles}
            {...props}
          />
        </Required>
      </div>
      <div className={`${styles['flexColumn']}`}>
        <h3 className={styles['leftMargin']}>Tickets</h3>
        <TicketInfo
          ticket={weightViolation}
        />
      </div>
      <div className={`${styles['flexRow']}`}>
        <CheckboxWithLabel
          formStyle={classes.formControl}
          checked={offloadRedistribution}
          onChange={(e) => handleChange(e.target.checked, 'UPDATE_PROPERTY', { property: 'offloadRedistribution' })}
          stateName={"offloadRedistribution"}
          title={"Offload/Redistribution"}
          disabled={disabled}
          userRoles={userRoles}
          {...props}
        />
        <Required
          required={missingFields.some(field => field?.name === "scales")}
          message={missingFields.find(field => field?.name === "scales")?.error}
        >
          <CustomAutoComplete
            disableCloseOnSelect={true}
            disableClearable={true}
            autoSelect={false}
            minWidth={"250px"}
            addMargin={true}
            title={"Scale Serial Numbers Used"}
            groupBy={(option) => option.user}
            options={officerScales.sort((a, b) => {
              if (a?.position < b?.position) {
                return -1
              } else {
                return 1
              }
            })}
            loading={false}
            error={""}
            multiple={true}
            value={scales ? _.uniqBy(officerScales?.filter(option => scales?.some(swe => option.id === swe.id)), "id") : []}
            name={"name"}
            stateName={"scales"}
            updateFields={(value) => updateScales(value)}
            id={"scales"}
            userRoles={userRoles}
            setPausePolling={setPausePolling}
            disabled={disabled}
            {...props}
          />
        </Required>
      </div>
      <div className={`${styles['flexRow']}`}>
        <Required
          required={missingFields.some(field => field?.name === "endedAt")}
          message={missingFields.find(field => field?.name === "endedAt")?.error}
        >
          <WrappedKeyboardDateTimePicker
            clearable={true}
            showTodayButton={true}
            label='Date & Time Ended'
            onFocus={() => setPausePolling(true)}
            onBlur={() => setPausePolling(false)}
            value={endedAt}
            onChange={(newValue, str, date) => handleChange(date, 'UPDATE_PROPERTY', { property: 'endedAt' })}
            format="MM/DD/YYYY HH:mm"
            ampm={false}
            disabled={disabled}
            className={classes.smallMatchMargin}
          />
        </Required>
      </div>
    </CollapsingHeader>
  );
}

export default OutcomeSection;

const TicketInfo = ({ ticket }) => {
  return (
    <div className={`${styles['flexRow']} ${styles['bordered']}`}>
      <TextField
        InputLabelProps={{ shrink: true }}
        margin='dense'
        id="ticketType-id"
        label={"Ticket\xa0Number"}
        value={ticket?.ticketNumber || ""}
        disabled={true}
      />

      <TextField
        InputLabelProps={{ shrink: true }}
        margin='dense'
        id="violation-name-id"
        label={"Violation\xa0Name"}
        value={ticket?.ticketViolationPrototype?.fullName || ticket?.ticketViolationPrototype?.name || ""}
        style={{ width: '450px' }}
        disabled={true}
      />
    </div>
  )
}
